import * as muiColors from "@mui/material/colors";
import {
  CREATION_DAYS_LEGEND_LABELS,
  DEFAULT_MAP_LEGENDS,
  LAST_VISIT_DAYS_LEGEND_LABELS,
} from "fieldpro-tools";
import _ from "lodash";

import * as colors from "assets/colors";
import TLang from "model/application/Lang";

import { ICustomerLegendFE } from "./types";

export const getLegendFilterKey = (tag: string, filterParams: object) =>
  `${tag}__${JSON.stringify(filterParams)}`;

export function prepareLegendForFE(legend: ICustomerLegendFE, lang: TLang) {
  legend.categories.forEach((cat) => {
    if (cat.key === null) {
      cat.key = "null";
    }
  });
  setLegendColors(legend);
  translateLegendLabels(legend, lang);
  return legend;
}
export function setLegendColors(legend: ICustomerLegendFE) {
  switch (legend.tag) {
    case DEFAULT_MAP_LEGENDS.ACTIVITY_DAY:
    case DEFAULT_MAP_LEGENDS.VISIT_DAY: {
      // Color mapping for last visit/activity date legend
      const colorMap: Record<string, string> = {
        [LAST_VISIT_DAYS_LEGEND_LABELS.TODAY]: colors.mintGreen,
        [LAST_VISIT_DAYS_LEGEND_LABELS.YESTERDAY]: colors.jungleGreen,
        [LAST_VISIT_DAYS_LEGEND_LABELS.LAST_7_DAYS]: colors.royalBlue,
        [LAST_VISIT_DAYS_LEGEND_LABELS.LAST_15_DAYS]: colors.lavenderBlue,
        [LAST_VISIT_DAYS_LEGEND_LABELS.LAST_30_DAYS]: colors.saffronYellow,
        [LAST_VISIT_DAYS_LEGEND_LABELS.MORE_THAN_30_DAYS]: colors.vibrantOrange,
        [LAST_VISIT_DAYS_LEGEND_LABELS.NEVER]: colors.fireEngineRed,
      };
      legend.categories.forEach((cat) => {
        if (Object.prototype.hasOwnProperty.call(colorMap, cat.label)) {
          cat.color = colorMap[cat.label];
        } else if (
          !_.isEmpty(
            _.intersection(
              [cat.label, cat.key],
              ["no_name", "no_owner", "null"],
            ),
          )
        ) {
          cat.color = colors.darkgray;
        }
      });
      break;
    }
    case DEFAULT_MAP_LEGENDS.CREATION_DAY: {
      // Color mapping for customer creation timeline legend
      const creationColorMap: Record<string, string> = {
        [CREATION_DAYS_LEGEND_LABELS.TODAY]: colors.mintGreen,
        [CREATION_DAYS_LEGEND_LABELS.YESTERDAY]: colors.jungleGreen,
        [CREATION_DAYS_LEGEND_LABELS.LAST_7_DAYS]: colors.royalBlue,
        [CREATION_DAYS_LEGEND_LABELS.LAST_30_DAYS]: colors.saffronYellow,
        [CREATION_DAYS_LEGEND_LABELS.MORE_THAN_30_DAYS]: colors.vibrantOrange,
      };
      legend.categories.forEach((cat) => {
        if (Object.prototype.hasOwnProperty.call(creationColorMap, cat.label)) {
          cat.color = creationColorMap[cat.label];
        } else if (
          !_.isEmpty(
            _.intersection(
              [cat.label, cat.key],
              ["no_name", "no_owner", "null"],
            ),
          )
        ) {
          cat.color = colors.darkgray;
        }
      });
      break;
    }
    default: {
      const c = [
        colors.tealLight,
        colors.yellow,
        colors.lightpurple,
        colors.blue,
        colors.BurntOrange,
        colors.GreyDark,
      ];
      const extraColors = _.without(
        _.flatMap(_.values(muiColors), _.values),
        ...c,
        colors.darkgray,
      );
      legend.categories.forEach((cat, index) => {
        if (
          !_.isEmpty(
            _.intersection(
              [cat.label, cat.key],
              ["no_name", "no_owner", "null"],
            ),
          )
        ) {
          cat.color = colors.darkgray;
        } else {
          cat.color = index < c.length ? c[index] : _.sample(extraColors);
        }
      });
      break;
    }
  }
  return legend;
}

export function translateLegendLabels(legend: ICustomerLegendFE, lang: TLang) {
  switch (legend.tag) {
    case DEFAULT_MAP_LEGENDS.ACTIVITY_DAY:
    case DEFAULT_MAP_LEGENDS.VISIT_DAY: {
      // Translate last visit/activity date labels
      const labelMap: Record<string, string> = {
        [LAST_VISIT_DAYS_LEGEND_LABELS.TODAY]:
          lang.components.maps.legend.labels.today,
        [LAST_VISIT_DAYS_LEGEND_LABELS.YESTERDAY]:
          lang.components.maps.legend.labels.yesterday,
        [LAST_VISIT_DAYS_LEGEND_LABELS.LAST_7_DAYS]:
          lang.components.maps.legend.labels.last7Days,
        [LAST_VISIT_DAYS_LEGEND_LABELS.LAST_15_DAYS]:
          lang.components.maps.legend.labels.last15Days,
        [LAST_VISIT_DAYS_LEGEND_LABELS.LAST_30_DAYS]:
          lang.components.maps.legend.labels.last30Days,
        [LAST_VISIT_DAYS_LEGEND_LABELS.MORE_THAN_30_DAYS]:
          lang.components.maps.legend.labels.moreThan30Days,
        [LAST_VISIT_DAYS_LEGEND_LABELS.NEVER]:
          lang.components.maps.legend.labels.never,
      };
      legend.categories.forEach((cat) => {
        if (Object.prototype.hasOwnProperty.call(labelMap, cat.label)) {
          cat.label = labelMap[cat.label];
        }
      });
      break;
    }
    case DEFAULT_MAP_LEGENDS.CREATION_DAY: {
      // Translate creation date labels
      const creationLabelMap: Record<string, string> = {
        [CREATION_DAYS_LEGEND_LABELS.TODAY]:
          lang.components.maps.legend.labels.createdToday,
        [CREATION_DAYS_LEGEND_LABELS.YESTERDAY]:
          lang.components.maps.legend.labels.createdYesterday,
        [CREATION_DAYS_LEGEND_LABELS.LAST_7_DAYS]:
          lang.components.maps.legend.labels.createdLast7Days,
        [CREATION_DAYS_LEGEND_LABELS.LAST_30_DAYS]:
          lang.components.maps.legend.labels.createdLast30Days,
        [CREATION_DAYS_LEGEND_LABELS.MORE_THAN_30_DAYS]:
          lang.components.maps.legend.labels.createdMoreThan30Days,
      };
      legend.categories.forEach((cat) => {
        if (Object.prototype.hasOwnProperty.call(creationLabelMap, cat.label)) {
          cat.label = creationLabelMap[cat.label];
        }
      });
      break;
    }
    case "_users":
      const cat = legend.categories.find((c) => c.label === "no_name");
      if (cat) cat.label = lang.components.maps.legend.unknownUser;
      break;
    case "_teams":
      const catTeam = legend.categories.find((c) => c.label === "no_name");
      if (catTeam) catTeam.label = lang.genericTerms.none;
      break;
    case "_owners":
      const catOwner = legend.categories.find((c) => c.label === "no_owner");
      if (catOwner) catOwner.label = lang.genericTerms.none;
      break;
    default:
      const catType = legend.categories.find((c) => c.key === "null");
      if (catType) catType.label = lang.genericTerms.none;
      break;
  }
}
